import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { questionTemplates } from './questionTemplates';
import { populateTemplatePlaceholders } from './utils';
import { useAssignmentNames } from '../../utils/queries';

export default function TemplateModal({
  open,
  onClose,
  onTemplateSelected,
  courseId,
  apiHost,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});

  const { data: assignmentNames = [] } = useAssignmentNames({
    courseId,
    apiHost,
  });

  useEffect(() => {
    if (selectedTemplate) {
      setTimeout(() => {
        const firstInput = document.querySelector('#template-input-0');
        if (firstInput) firstInput.focus();
      }, 100);
    }
  }, [selectedTemplate]);

  const handleTemplateSelect = (template) => {
    if (template.placeholders.length > 0) {
      setSelectedTemplate(template);
      setFormValues(
        template.placeholders.reduce(
          (acc, key) => ({
            ...acc,
            [key]: '',
          }),
          {},
        ),
      );
      setErrors({});
    } else {
      onTemplateSelected(template.value);
      handleClose();
    }
  };

  const handleInputChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validateInputs = () => {
    let isValid = true;
    const newErrors = {};

    selectedTemplate.placeholders.forEach((key) => {
      if (!formValues[key]) {
        newErrors[key] = `Please enter a value for ${key}`;
        isValid = false;
      } else if (
        key === 'percentage' &&
        (isNaN(formValues[key]) || formValues[key] < 0 || formValues[key] > 100)
      ) {
        newErrors[key] = 'Percentage must be a number between 0 and 100';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (!validateInputs()) return;

    const finalText = populateTemplatePlaceholders(
      selectedTemplate.value,
      formValues,
    );
    onTemplateSelected(finalText);
    handleClose();
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setFormValues({});
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={selectedTemplate ? 'sm' : 'md'}
    >
      <DialogTitle>
        {selectedTemplate
          ? `Complete Template: ${selectedTemplate.label}`
          : 'Select a Question Template'}
      </DialogTitle>

      <DialogContent>
        {selectedTemplate ? (
          <>
            {selectedTemplate.placeholders.map((key, index) => {
              if (key === 'assignment name') {
                return (
                  <Autocomplete
                    key={key}
                    options={assignmentNames}
                    inputValue={formValues[key] || ''}
                    onInputChange={(_event, newInputValue) => {
                      handleInputChange(key, newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id={`template-input-${index}`}
                        label={`Enter ${key}`}
                        fullWidth
                        margin="dense"
                        error={!!errors[key]}
                        helperText={errors[key]}
                      />
                    )}
                    freeSolo
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                  />
                );
              }
              return (
                <TextField
                  key={key}
                  id={`template-input-${index}`}
                  label={`Enter ${key}`}
                  fullWidth
                  margin="dense"
                  value={formValues[key] || ''}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  error={!!errors[key]}
                  helperText={errors[key]}
                  type={key === 'percentage' ? 'number' : 'text'}
                />
              );
            })}

            <div style={{ marginTop: 16, fontStyle: 'italic', color: '#555' }}>
              Preview:
              <br />
              <span style={{ fontWeight: 'bold' }}>
                {populateTemplatePlaceholders(
                  selectedTemplate.value,
                  formValues,
                )}
              </span>
            </div>
          </>
        ) : (
          <List>
            {questionTemplates.map((template) => (
              <ListItem
                button
                key={template.value}
                onClick={() => handleTemplateSelect(template)}
                data-test-id={template.dataTestId}
              >
                <ListItemText
                  primary={template.label}
                  secondary={template.value}
                  primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                  secondaryTypographyProps={{
                    style: { fontStyle: 'italic', color: '#777' },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        {selectedTemplate ? (
          <>
            <Button onClick={() => setSelectedTemplate(null)} color="secondary">
              Back
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              Insert Question
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} color="secondary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

TemplateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTemplateSelected: PropTypes.func.isRequired,
  courseId: PropTypes.string,
  apiHost: PropTypes.string,
};
